import { watch } from "vue";

import { $t } from "@/i18n";
import { useAsyncOperationProgress } from "@/store/asyncOperationProgress";
import { toPercentage } from "@/utils/progressPercentage";
import { showToastForError, showToastForSuccess } from "@/utils/toast";

import { useRefreshTrigger } from "./refreshTrigger";
import { progressEvent, useProgressEvents } from "./useProgressEvents";

/** The `kind` of a bulk update's operation in asyncOperationProgress; its id is the job id. */
export const RECORD_BULK_UPDATE_OPERATION = "record-bulk-update";

const EVENT_CODE_PROGRESS = "record_bulk_update.progress";
const EVENT_CODE_FINISHED = "record_bulk_update.finished";

interface BulkUpdateProgressPayload {
  jobId: string;
  total: number;
  completed: number;
}

interface BulkUpdateFinishedPayload {
  jobId: string;
  total: number;
  updatedCount: number;
  failedCount: number;
}

function isProgressPayload(data: unknown): data is BulkUpdateProgressPayload {
  const payload = data as BulkUpdateProgressPayload | null;
  return (
    typeof payload?.jobId === "string" && typeof payload.total === "number" && typeof payload.completed === "number"
  );
}

function isFinishedPayload(data: unknown): data is BulkUpdateFinishedPayload {
  const payload = data as BulkUpdateFinishedPayload | null;
  return (
    typeof payload?.jobId === "string" &&
    typeof payload.total === "number" &&
    typeof payload.updatedCount === "number" &&
    typeof payload.failedCount === "number"
  );
}

/** Feeds bulk update operations from the SSE stream, matched by job id. */
export default function useRecordBulkUpdateEvents() {
  const progress = useAsyncOperationProgress();
  const refetchList = useRefreshTrigger();

  // A small job can finish before the request that started it returns its job id.
  const finishedEarly = new Map<string, BulkUpdateFinishedPayload>();

  function isTracked(jobId: string) {
    return progress.operations.some((o) => o.id === jobId && o.kind === RECORD_BULK_UPDATE_OPERATION);
  }

  function finish(data: BulkUpdateFinishedPayload) {
    refetchList();

    if (data.updatedCount === 0) {
      progress.remove(data.jobId);
      showToastForError($t("record.bulkEdit.message.failed"));
      return;
    }
    // A job that stopped midway reports only the failures it reached; the rest were not updated either.
    const notUpdated = Math.max(data.failedCount, data.total - data.updatedCount);
    if (notUpdated > 0) {
      progress.remove(data.jobId);
      showToastForError($t("record.bulkEdit.message.partlyFailed", { updated: data.updatedCount, failed: notUpdated }));
      return;
    }
    progress.finish(data.jobId);
    showToastForSuccess($t("record.bulkEdit.message.updated", { count: data.updatedCount }));
  }

  const events = useProgressEvents([
    progressEvent(EVENT_CODE_PROGRESS, isProgressPayload, (data) => {
      progress.update(data.jobId, { percentage: toPercentage(data.completed, data.total) });
    }),
    progressEvent(EVENT_CODE_FINISHED, isFinishedPayload, (data) => {
      if (isTracked(data.jobId)) finish(data);
      else finishedEarly.set(data.jobId, data);
    }),
  ]);

  const stopWatch = watch(
    () => progress.operations.map((o) => o.id),
    (ids) => {
      for (const id of ids) {
        const early = finishedEarly.get(id);
        if (!early || !isTracked(id)) continue;
        finishedEarly.delete(id);
        finish(early);
      }
    },
  );

  function close() {
    stopWatch();
    events.close();
  }

  return { close };
}
